<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import campaignMixin from 'shared/mixins/campaignMixin';
import ongoingCampaignMixin from 'shared/mixins/ongoingCampaignMixin';
import whatsappTemplateMixin from 'shared/mixins/whatsappTemplateMixin';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import CampaignTemplateParser from 'dashboard/components/widgets/conversation/WhatsappTemplates/CampaignTemplateParser.vue';

import CommonWhatsappIntervalsSelect from '../CommonWhatsappCampaign/CommonWhatsappIntervalsSelect.vue';
import CommonWhatsappCampaignMessage from '../CommonWhatsappCampaign/CommonWhatsappCampaignMessage.vue';
import CampaignReplyAssignmentSelect from '../CampaignReplyAssignmentSelect.vue';

const CRON_EXAMPLES = [
  { labelKey: 'EXAMPLE_DAILY', value: '0 12 * * *' },
  { labelKey: 'EXAMPLE_WEEKDAYS', value: '0 9 * * 1-5' },
  { labelKey: 'EXAMPLE_WEEKLY', value: '0 8 * * 1' },
  { labelKey: 'EXAMPLE_MONTHLY', value: '0 10 1 * *' },
];

export default {
  components: {
    TemplatesPicker,
    CampaignTemplateParser,
    CommonWhatsappIntervalsSelect,
    CommonWhatsappCampaignMessage,
    CampaignReplyAssignmentSelect,
  },
  mixins: [campaignMixin, whatsappTemplateMixin, ongoingCampaignMixin],
  props: {},
  emits: ['onClose'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      title: '',
      description: '',
      message: '',
      dataSourceUrl: '',
      selectedInbox: null,
      messageParams: [],
      template: null, // selected WhatsApp Template
      filters: '',
      headers: '',
      selectedInterval: [],
      selectedAgentBotId: null,
      replyAssignment: { reply_assignment: 'inbox_default' },
      customRecurrency: '',
    };
  },

  computed: {
    ...mapGetters({
      uiFlags: 'ongoingCampaigns/getUIFlags',
      currentUser: 'getCurrentUser',
      campaigns: 'ongoingCampaigns/getAllCampaigns',
      agentBots: 'agentBots/getBots',
    }),
    inboxSelected: {
      get() {
        if (this.selectedInbox) return this.selectedInbox.id;
        return false;
      },
      set(value) {
        this.selectedInbox = value;
      },
    },
    badCampaignParams() {
      return (
        this.v$.$invalid ||
        this.isCommonWhatsappConfigInvalid ||
        this.templateWithMediaAndAttachmentWasNotUploaded
      );
    },
    cronExamples() {
      return CRON_EXAMPLES.map(e => ({
        label: this.$t(
          `CAMPAIGN.ONGOING.MODAL.CUSTOM_RECURRENCY.${e.labelKey}`
        ),
        value: e.value,
      }));
    },
  },
  methods: {
    onClose() {
      this.$emit('onClose');
    },
    onChangeMessageCommonWhatsapp(event) {
      this.commonWhatsappMessage = event;
      this.message = event.text;
    },
    onChangeIntervalSelected(event) {
      this.selectedInterval = event;
    },
    getCampaignDetails() {
      const campaignDetails = {
        title: this.title,
        data_source: this.dataSourceUrl,
        cron_time: this.normalizeCronExpression(this.customRecurrency),
        inbox_id: this.selectedInbox.id,
        message: this.isCommonWhatsappCampaign
          ? { text: this.commonWhatsappMessage.text }
          : this.template,
        additional_infos: this.additionalInfosData(),
        agent_bot_id: this.selectedAgentBotId,
        ...this.replyAssignment,
        description: this.description,
      };
      if (this.attachedFiles.length)
        campaignDetails.attachment = this.attachedFiles[0]?.resource.file;
      return campaignDetails;
    },
    // eslint-disable-next-line consistent-return
    async addCampaign() {
      try {
        const campaignDetails = this.getCampaignDetails();
        if (this.canNotRequestWithCurrentParams() || !campaignDetails.message)
          return useAlert(this.$t('CAMPAIGN.ONGOING.MODAL.PARAMS_ERROR'));

        await this.$store.dispatch('ongoingCampaigns/create', campaignDetails);
        useAlert(this.$t('CAMPAIGN.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (err) {
        useAlert(this.$t('CAMPAIGN.ADD.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col">
    <woot-modal-header
      :header-title="$t('CAMPAIGN.ONGOING.ADD.TITLE')"
      :header-content="$t('CAMPAIGN.ONGOING.ADD.DESC')"
    />
    <form class="flex flex-col w-full" @submit.prevent="addCampaign">
      <div class="w-full">
        <!-- START TITLE INPUT -->
        <woot-input
          v-model="title"
          :label="$t('CAMPAIGN.ADD.FORM.TITLE.LABEL')"
          type="text"
          :class="{ error: v$.title.$error }"
          :error="v$.title.$error ? $t('CAMPAIGN.ADD.FORM.TITLE.ERROR') : ''"
          :placeholder="$t('CAMPAIGN.ADD.FORM.TITLE.PLACEHOLDER')"
          @blur="v$.title.$touch"
        />
        <!-- END TITLE INPUT -->

        <!-- START DESCRIPTION SECTION -->
        <label>
          {{ $t('CAMPAIGN.ONGOING.MODAL.DESCRIPTION') }}
          <textarea
            v-model="description"
            rows="2"
            type="text"
            :placeholder="$t('CAMPAIGN.ONGOING.MODAL.DESCRIPTION_PLACEHOLDER')"
          />
        </label>
        <!-- END DESCRIPTION SECTION -->

        <!-- START RECURRENCY -->
        <woot-input
          v-model="customRecurrency"
          :label="$t('CAMPAIGN.ONGOING.MODAL.CUSTOM_RECURRENCY.LABEL')"
          type="text"
          :class="{ error: v$.customRecurrency.$error }"
          :error="
            v$.customRecurrency.$error
              ? $t('CAMPAIGN.ONGOING.MODAL.CUSTOM_RECURRENCY.ERROR')
              : ''
          "
          :placeholder="
            $t('CAMPAIGN.ONGOING.MODAL.CUSTOM_RECURRENCY.PLACEHOLDER')
          "
          @blur="v$.customRecurrency.$touch"
        />
        <div class="mb-4 flex flex-wrap gap-1">
          <span class="w-full text-xs text-slate-500 dark:text-slate-400">
            {{ $t('CAMPAIGN.ONGOING.MODAL.CUSTOM_RECURRENCY.EXAMPLES_LABEL') }}
          </span>
          <button
            v-for="example in cronExamples"
            :key="example.value"
            type="button"
            class="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600 hover:border-woot-400 hover:bg-woot-50 hover:text-woot-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
            @click="customRecurrency = example.value"
          >
            {{ example.label }}
            <span class="ml-1 font-mono opacity-60">{{ example.value }}</span>
          </button>
        </div>
        <!-- END RECURRENCY -->

        <!-- START DATA SOURCE INPUT -->
        <woot-input
          v-model="dataSourceUrl"
          :label="$t('CAMPAIGN.ONGOING.MODAL.DATA_SOURCE.LABEL')"
          type="text"
          :class="{ error: v$.dataSourceUrl.$error }"
          :error="
            v$.dataSourceUrl.$error
              ? $t('CAMPAIGN.ONGOING.MODAL.DATA_SOURCE.ERROR')
              : ''
          "
          :placeholder="$t('CAMPAIGN.ONGOING.MODAL.DATA_SOURCE.PLACEHOLDER')"
          @blur="v$.dataSourceUrl.$touch"
        />
        <!-- END DATA SOURCE INPUT -->

        <!-- START INBOX MULTISELECT -->
        <label :class="{ error: v$.selectedInbox.$error }">
          {{ $t('CAMPAIGN.ADD.FORM.INBOX.LABEL') }}
          <select v-model="selectedInbox">
            <option
              v-for="item in campaignInboxes"
              :key="item.name"
              :value="item"
            >
              {{ item.name }}
            </option>
          </select>
          <span v-if="v$.selectedInbox.$error" class="message">
            {{ $t('CAMPAIGN.ADD.FORM.INBOX.ERROR') }}
          </span>
        </label>
        <!-- END INBOX MULTISELECT -->

        <!-- START MESSAGE LABEL -->
        <div v-if="hasWhatsappTemplates">
          <TemplatesPicker
            v-if="!template"
            :inbox-id="inboxSelected"
            @click.stop
            @on-select="toggleWaTemplate"
          />
          <CampaignTemplateParser
            v-else
            :template="template"
            :csv-headers="[]"
            :first-example="{}"
            is-csv-audience
            is-ongoing
            @template-update="onTemplateUpdate"
            @attachments-change="updateAttachments"
          />
          <!-- RESET TEMPLATE -->
          <woot-button
            v-if="template"
            ref="arrowDownButton"
            color-scheme="secondary"
            class="mt-2 mb-3 flex"
            icon="chevron-down"
            @click.prevent
            @click="resetWaTemplateSelection"
          >
            {{ $t('CAMPAIGN.TEMPLATE_PARSER.CHOOSE_ANOTHER_BUTTON') }}
          </woot-button>
        </div>

        <div v-else-if="isCommonWhatsappCampaign">
          <CommonWhatsappCampaignMessage
            origin-params
            :is-csv-audience="false"
            @on-change-message="onChangeMessageCommonWhatsapp"
          />

          <CommonWhatsappIntervalsSelect
            @selected-interval="onChangeIntervalSelected"
          />
        </div>
        <!-- END MESSAGE LABEL -->
      </div>

      <!-- START BOT MULTISELECT -->
      <label>
        {{ $t('CAMPAIGN.ADD.FORM.BOT.LABEL') }}
        <select v-model="selectedAgentBotId">
          <option :value="null" selected>
            {{ $t('CAMPAIGN.ADD.FORM.BOT.INBOX_DEFAULT_OPTION') }}
          </option>
          <option
            v-for="agentBot in agentBots"
            :key="agentBot.id"
            :value="agentBot.id"
          >
            {{ agentBot.name }}
          </option>
        </select>
      </label>
      <!-- END BOT MULTISELECT -->

      <CampaignReplyAssignmentSelect
        v-model="replyAssignment"
        :inbox-id="selectedInbox?.id"
      />

      <!-- START FILTERS INPUT -->
      <woot-input
        v-model="filters"
        :label="$t('CAMPAIGN.ONGOING.MODAL.FILTERS.LABEL')"
        type="text"
        :class="{ error: v$.filters.$error }"
        :error="
          v$.filters.$error ? $t('CAMPAIGN.ONGOING.MODAL.FILTERS.ERROR') : ''
        "
        :placeholder="$t('CAMPAIGN.ONGOING.MODAL.FILTERS.PLACEHOLDER')"
        @blur="v$.filters.$touch"
      />
      <!-- END FILTERS INPUT -->

      <!-- START AUTHENTICATION HEADER INPUT -->
      <woot-input
        v-model="headers"
        :label="$t('CAMPAIGN.ONGOING.MODAL.HEADERS.LABEL')"
        type="text"
        :class="{ error: v$.headers.$error }"
        :error="
          v$.headers.$error ? $t('CAMPAIGN.ONGOING.MODAL.HEADERS.ERROR') : ''
        "
        :placeholder="$t('CAMPAIGN.ONGOING.MODAL.HEADERS.PLACEHOLDER')"
        @blur="v$.headers.$touch"
      />
      <!-- END AUTHENTICATION HEADERS INPUT -->

      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <woot-button
          :is-loading="uiFlags.isCreating"
          :disabled="badCampaignParams"
          type="submit"
        >
          {{ $t('CAMPAIGN.ADD.CREATE_BUTTON_TEXT') }}
        </woot-button>
        <woot-button variant="clear" @click.prevent="onClose">
          {{ $t('CAMPAIGN.ADD.CANCEL_BUTTON_TEXT') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>
